export type MotionPreference = 'auto' | 'full' | 'reduced';
export type UIScreen = 'title' | 'playing' | 'paused' | 'gameover';

export interface StoredSettings {
  muted: boolean;
  motion: MotionPreference;
}

export interface HUDState {
  score: number;
  combo: number;
  relay: number;
  focus: number;
  pressure: number;
  overdrive: number;
}

export interface GameOverSummary {
  score: number;
  best: number;
  relay: number;
  perfects: number;
  bankShots: number;
  newBest: boolean;
  cause: 'hazard' | 'darkness' | 'lost';
}

interface UIHandlers {
  onStart: () => void;
  onRetry: () => void;
  onPause: () => void;
  onResume: () => void;
  onRestart: () => void;
  onHome: () => void;
  onSettings: (settings: StoredSettings) => void;
}

const icons = {
  pause: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 6v12M16 6v12"/></svg>',
  sound: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 10v4h4l5 4V6L9 10H5Z"/><path class="sound-waves" d="M17 9c1.2 1.5 1.2 4.5 0 6M19.5 6.5c3 3 3 8 0 11"/></svg>',
  retry: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 8a8 8 0 1 0 1 7M19 8V3m0 5h-5"/></svg>',
  close: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 7 10 10M17 7 7 17"/></svg>',
  beam: '<svg viewBox="0 0 80 32" aria-hidden="true"><path d="M5 25 31 7l16 13L75 4"/><circle cx="5" cy="25" r="4"/><circle cx="75" cy="4" r="5"/></svg>',
};

export class UIController {
  readonly root: HTMLElement;
  readonly live: HTMLElement;
  private readonly handlers: UIHandlers;
  private screen: UIScreen = 'title';
  private settings: StoredSettings;
  private coachTimer = 0;
  private lastHud = '';

  constructor(root: HTMLElement, live: HTMLElement, handlers: UIHandlers, settings: StoredSettings) {
    this.root = root;
    this.live = live;
    this.handlers = handlers;
    this.settings = { ...settings };
    this.root.innerHTML = `
      <section class="hud is-hidden" data-ui="hud" aria-label="Spielstand">
        <div class="hud-score"><span>PULS</span><strong data-ui="score">0</strong></div>
        <div class="hud-center">
          <span class="relay-label" data-ui="relay">RELAY 01</span>
          <div class="pressure-track" aria-label="Abstand zur Dunkelzone">
            <i data-ui="pressure"></i><b aria-hidden="true"></b>
          </div>
        </div>
        <button class="icon-button" data-action="pause" aria-label="Pause">${icons.pause}</button>
        <div class="combo-pill is-hidden" data-ui="combo"><small>FLOW</small><strong>×1</strong></div>
        <div class="overdrive-bar" data-ui="overdrive" aria-label="Overdrive"><i></i></div>
      </section>

      <section class="screen title-screen" data-ui="title">
        <div class="title-topline"><span></span>THE LIGHT REMEMBERS<span></span></div>
        <div class="brand-lockup">
          <div class="brand-glyph" aria-hidden="true">${icons.beam}</div>
          <h1><span>LUX</span><em>RELAY</em></h1>
          <p>Präzision wird Momentum.</p>
        </div>
        <div class="title-actions">
          <div class="best-line"><span>BESTER PULS</span><strong data-ui="title-best">0</strong></div>
          <button class="primary-button" data-action="start"><span>SPIELEN</span><i aria-hidden="true">↗</i></button>
          <button class="text-button" data-action="open-settings">EINSTELLUNGEN</button>
        </div>
        <div class="version-mark">ORIGINAL ARCADE SYSTEM · 01</div>
      </section>

      <div class="coach is-hidden" data-ui="coach" role="status">
        <small data-ui="coach-kicker">KALIBRIERUNG</small>
        <strong data-ui="coach-title">AUFLEGEN · ZIEHEN</strong>
        <span data-ui="coach-detail">Loslassen feuert den Puls</span>
      </div>

      <section class="screen pause-screen is-hidden" data-ui="paused" aria-labelledby="pause-title">
        <div class="glass-panel compact-panel">
          <small>SIGNAL GEHALTEN</small>
          <h2 id="pause-title">PAUSE</h2>
          <button class="primary-button" data-action="resume"><span>WEITER</span><i>↗</i></button>
          <button class="secondary-button" data-action="restart">NEU STARTEN</button>
          <button class="text-button" data-action="open-settings">EINSTELLUNGEN</button>
        </div>
      </section>

      <section class="screen result-screen is-hidden" data-ui="gameover" aria-labelledby="result-title">
        <div class="result-heading">
          <small data-ui="result-kicker">SIGNAL VERLOREN</small>
          <h2 id="result-title" data-ui="result-score">0</h2>
          <span>PULS</span>
        </div>
        <div class="result-grid">
          <div><span>BESTE</span><strong data-ui="result-best">0</strong></div>
          <div><span>RELAY</span><strong data-ui="result-relay">0</strong></div>
          <div><span>PERFEKT</span><strong data-ui="result-perfect">0</strong></div>
          <div><span>BANDEN</span><strong data-ui="result-banks">0</strong></div>
        </div>
        <div class="result-actions">
          <button class="primary-button" data-action="retry">${icons.retry}<span>NOCH EIN PULS</span></button>
          <button class="text-button" data-action="home">ZURÜCK</button>
        </div>
      </section>

      <section class="settings-sheet is-hidden" data-ui="settings" aria-labelledby="settings-title">
        <button class="sheet-backdrop" data-action="close-settings" aria-label="Einstellungen schließen"></button>
        <div class="glass-panel settings-panel">
          <header><div><small>SYSTEM</small><h2 id="settings-title">EINSTELLUNGEN</h2></div><button class="icon-button" data-action="close-settings" aria-label="Schließen">${icons.close}</button></header>
          <div class="setting-row">
            <div><strong>Audio</strong><span>Synthetisierte Musik & Effekte</span></div>
            <button class="toggle-button" data-action="toggle-sound" aria-label="Audio an oder aus" aria-pressed="true">${icons.sound}<span data-ui="sound-label">AN</span></button>
          </div>
          <div class="setting-block">
            <div><strong>Bewegung</strong><span>Kamera, Partikel und Lichtimpulse</span></div>
            <div class="segmented" role="group" aria-label="Bewegung">
              <button data-motion="auto">AUTO</button><button data-motion="full">VOLL</button><button data-motion="reduced">RUHIG</button>
            </div>
          </div>
          <p class="settings-note">Das Spiel funktioniert vollständig ohne Ton. Status wird zusätzlich über Form und Text vermittelt.</p>
        </div>
      </section>
    `;
    this.bind();
    this.syncSettings();
  }

  showTitle(best: number): void {
    this.screen = 'title';
    this.hideScreens();
    this.get('title').classList.remove('is-hidden');
    this.get('title-best').textContent = best.toLocaleString('de-DE');
    this.get('hud').classList.add('is-hidden');
    this.hideCoach();
  }

  showPlaying(): void {
    this.screen = 'playing';
    this.hideScreens();
    this.get('hud').classList.remove('is-hidden');
  }

  showPaused(fromSystem = false): void {
    this.screen = 'paused';
    this.hideScreens();
    this.get('paused').classList.remove('is-hidden');
    const kicker = this.get('paused').querySelector('small');
    if (kicker) kicker.textContent = fromSystem ? 'AUTOMATISCH GEHALTEN' : 'SIGNAL GEHALTEN';
    this.announce('Spiel pausiert');
  }

  showGameOver(summary: GameOverSummary): void {
    this.screen = 'gameover';
    this.hideCoach();
    this.hideScreens();
    this.get('hud').classList.add('is-hidden');
    this.get('gameover').classList.remove('is-hidden');
    this.get('result-kicker').textContent = summary.newBest ? 'NEUER BESTWERT' : summary.cause === 'darkness' ? 'DUNKELZONE ERREICHT' : 'SIGNAL VERLOREN';
    this.get('result-score').textContent = summary.score.toLocaleString('de-DE');
    this.get('result-best').textContent = summary.best.toLocaleString('de-DE');
    this.get('result-relay').textContent = String(summary.relay).padStart(2, '0');
    this.get('result-perfect').textContent = String(summary.perfects);
    this.get('result-banks').textContent = String(summary.bankShots);
    this.announce(`Lauf beendet. ${summary.score} Punkte.`);
  }

  updateHud(state: HUDState): void {
    const fingerprint = `${state.score}|${state.combo}|${state.relay}|${Math.round(state.focus * 100)}|${Math.round(state.pressure * 100)}|${Math.round(state.overdrive * 100)}`;
    if (fingerprint === this.lastHud) return;
    this.lastHud = fingerprint;
    this.get('score').textContent = state.score.toLocaleString('de-DE');
    this.get('relay').textContent = `RELAY ${String(state.relay).padStart(2, '0')}`;
    this.get('pressure').setAttribute('style', `transform:scaleX(${Math.max(0, Math.min(1, state.pressure))})`);
    const combo = this.get('combo');
    combo.classList.toggle('is-hidden', state.combo < 2);
    const comboValue = combo.querySelector('strong');
    if (comboValue) comboValue.textContent = `×${state.combo}`;
    this.get('overdrive').classList.toggle('is-active', state.overdrive > 0);
    const overdriveFill = this.get('overdrive').querySelector('i');
    if (overdriveFill) overdriveFill.setAttribute('style', `transform:scaleX(${Math.max(0, Math.min(1, state.overdrive))})`);
    document.documentElement.style.setProperty('--focus', String(Math.max(0, Math.min(1, state.focus))));
  }

  showCoach(title: string, detail: string, kicker = 'KALIBRIERUNG', duration = 2600): void {
    window.clearTimeout(this.coachTimer);
    this.get('coach-kicker').textContent = kicker;
    this.get('coach-title').textContent = title;
    this.get('coach-detail').textContent = detail;
    this.get('coach').classList.remove('is-hidden');
    if (duration > 0) this.coachTimer = window.setTimeout(() => this.hideCoach(), duration);
  }

  hideCoach(): void {
    window.clearTimeout(this.coachTimer);
    this.get('coach').classList.add('is-hidden');
  }

  announce(message: string): void {
    this.live.textContent = '';
    window.setTimeout(() => { this.live.textContent = message; }, 20);
  }

  getScreen(): UIScreen {
    return this.screen;
  }

  getSettings(): StoredSettings {
    return { ...this.settings };
  }

  private bind(): void {
    this.root.addEventListener('click', (event) => {
      const target = (event.target as HTMLElement).closest<HTMLElement>('[data-action]');
      if (!target) return;
      const action = target.dataset.action;
      if (action === 'start') this.handlers.onStart();
      if (action === 'retry') this.handlers.onRetry();
      if (action === 'pause') this.handlers.onPause();
      if (action === 'resume') this.handlers.onResume();
      if (action === 'restart') this.handlers.onRestart();
      if (action === 'home') this.handlers.onHome();
      if (action === 'open-settings') this.openSettings();
      if (action === 'close-settings') this.closeSettings();
      if (action === 'toggle-sound') {
        this.settings.muted = !this.settings.muted;
        this.syncSettings();
        this.handlers.onSettings(this.getSettings());
      }
    });

    this.root.querySelectorAll<HTMLButtonElement>('[data-motion]').forEach((button) => {
      button.addEventListener('click', () => {
        const motion = button.dataset.motion;
        if (motion === 'auto' || motion === 'full' || motion === 'reduced') {
          this.settings.motion = motion;
          this.syncSettings();
          this.handlers.onSettings(this.getSettings());
        }
      });
    });
  }

  private openSettings(): void {
    this.get('settings').classList.remove('is-hidden');
  }

  private closeSettings(): void {
    this.get('settings').classList.add('is-hidden');
  }

  private syncSettings(): void {
    const soundButton = this.root.querySelector<HTMLButtonElement>('[data-action="toggle-sound"]');
    soundButton?.setAttribute('aria-pressed', String(!this.settings.muted));
    soundButton?.classList.toggle('is-off', this.settings.muted);
    this.get('sound-label').textContent = this.settings.muted ? 'AUS' : 'AN';
    this.root.querySelectorAll<HTMLButtonElement>('[data-motion]').forEach((button) => {
      button.classList.toggle('is-selected', button.dataset.motion === this.settings.motion);
      button.setAttribute('aria-pressed', String(button.dataset.motion === this.settings.motion));
    });
    this.root.dataset.motion = this.settings.motion;
  }

  private hideScreens(): void {
    for (const name of ['title', 'paused', 'gameover']) this.get(name).classList.add('is-hidden');
    this.get('settings').classList.add('is-hidden');
  }

  private get(name: string): HTMLElement {
    const element = this.root.querySelector<HTMLElement>(`[data-ui="${name}"]`);
    if (!element) throw new Error(`Missing UI element: ${name}`);
    return element;
  }
}
